import React, { useState } from 'react'
import { Box, Button } from '@mui/material'
import { mixed, object } from 'yup'
import { useQueryClient } from '@tanstack/react-query'

import { useDialog } from '@/hooks'
import { Dialog } from '@/components'
import { B2CRegistryInitForm } from '@/@types/b2c.types.ts'
import { useGetRegistryTemplate, useMutateB2cRegistryInit } from '@/services'
import { downloadFile, preventHandler } from '@/utils.ts'
import { Form, FormSchema, FormSchemaMessages } from '@/components/form'

import { B2CRegistryDialogFileUpload } from './b2c-registry-dialog-file-upload.tsx'
import { B2CConfirmDialog } from './b2c-confirm-dialog.tsx'
import { B2CRegistryNotifyDialog } from './b2c-registry-notify-dialog.tsx'

const b2cRegistryInitValidationSchema = object<FormSchema<B2CRegistryInitForm>>({
  file: mixed<File>().required(FormSchemaMessages.required),
})

const B2C_REGISTRY_FORM_NAME = 'newB2CRegistryInit'
export const NewB2CRegistryDialog = (): React.ReactElement => {
  const [stage, setStage] = useState<'init' | 'confirm' | 'notify'>('init')
  const [templateValue, setTemplateValue] = useState<string>('xlsx')
  const qc = useQueryClient()

  const { showDialog, handleShowDialog, handleHideDialog } = useDialog()

  const { mutateAsync: mutateAsyncDomnFile } = useGetRegistryTemplate()
  const { reset, error, data, isPending, isError, mutateAsync } = useMutateB2cRegistryInit()
  const handleFormSubmit = async (formValues: B2CRegistryInitForm) => {
    try {
      const formData = new FormData()
      formData.append('file', formValues.file)
      await preventHandler(mutateAsync, isPending)(formData)
      setStage('confirm')
    } catch (e) {
      console.log()
    }
  }

  const handeCloseDialog = () => {
    qc.invalidateQueries({ queryKey: ['b2c_list'] })
    qc.invalidateQueries({ queryKey: ['b2c_registry_list'] })
    setStage('init')
    handleHideDialog()
    reset()
  }

  const handleGetTemplate = async (format: string) => {
    const response = await mutateAsyncDomnFile({
      formatRegistry: format,
    })

    if (response?.status === 200 && response.data) {
      const filename = response?.headers['content-disposition']?.match(/filename="([^"]+)"/i)

      downloadFile(URL.createObjectURL(response.data), filename?.[1] ?? `unknown`)
    }
  }

  const renderTitleDialog = () => {
    switch (stage) {
      case 'init':
        return 'Заявка на новый B2C перевод реестром'
      case 'confirm':
        return 'Введите одноразовый код'
      case 'notify':
        return 'Импорт файла успешно проведен'
    }
  }

  const renderContentDialog = () => {
    switch (stage) {
      case 'confirm':
        return <B2CConfirmDialog {...data?.data} onSuccess={() => setStage('notify')} />
      case 'init':
        return (
          <B2CRegistryDialogFileUpload
            onLoadTemplate={handleGetTemplate}
            onChangeTemplate={(e) => setTemplateValue(e.target.value)}
            isError={isError}
            error={error}
            templateValue={templateValue}
            formName={B2C_REGISTRY_FORM_NAME}
          />
        )
      case 'notify':
        return <B2CRegistryNotifyDialog onClose={handeCloseDialog} />
    }
  }

  return (
    <Box>
      <Button color="primary" onClick={handleShowDialog}>
        Переводы B2C реестром
      </Button>
      {showDialog && (
        <Form
          name={B2C_REGISTRY_FORM_NAME}
          validationSchema={b2cRegistryInitValidationSchema}
          onSubmit={handleFormSubmit}
        >
          <Dialog
            title={renderTitleDialog()}
            open={showDialog}
            onClose={handeCloseDialog}
            maxWidth="md"
            content={renderContentDialog()}
          />
        </Form>
      )}
    </Box>
  )
}
